import React, { CSSProperties, MouseEvent, ReactNode, useEffect, useRef, useState } from "react";

import { Select as AntSelect, SelectProps as AntSelectProps } from "antd";
import { RuleObject } from "antd/es/form";
import { NamePath } from "antd/es/form/interface";
import { BaseOptionType } from "antd/es/select";

import { parseInputRules } from "utils";

import { Form, Icon, InputLabel, Spin, Typography } from "components";

const getSuffixIcon = (showArrow: boolean | undefined, hasBeenFocused: boolean, showSearch: boolean | undefined, mode: AntSelectProps["mode"]): ReactNode => {
  if (hasBeenFocused) {
    if (!showArrow && (showSearch || mode === "tags" || mode === "multiple")) {
      return <Icon iconName="tabler/search" size="s" />;
    }
    return <Icon iconName="tabler/chevron-up" size="s" />;
  }
  return <Icon iconName="tabler/chevron-down" size="s" />;
};

/** SECTION: Private utility functions to make default showSearch behaviour work */

/**
 * @function sortFilteredOptions
 * @param optionA First compare item
 * @param optionB Second compare item
 * @param { searchValue } searchValue The current search term
 */
function sortFilteredOptions<T extends BaseOptionType>(optionA: T, optionB: T, { searchValue }: { searchValue: string }) {
  const search = searchValue.toLowerCase();
  const labelA = getOptionLabel(optionA).toLowerCase();
  const labelB = getOptionLabel(optionB).toLowerCase();
  const aStarts = labelA.startsWith(search);
  const bStarts = labelB.startsWith(search);
  if (aStarts === bStarts) return labelA.localeCompare(labelB);
  return aStarts ? -1 : 1;
}

/**
 * @function getOptionLabel
 * @description helper function to find most relevant search terms in an object
 */
function getOptionLabel<T extends BaseOptionType>(option: T) {
  // If the option is a string directly
  if (typeof option.children === "string") return option.children;
  // If the option has a label prop, use it first
  if (typeof option.label === "string") return option.label;

  // Check if our option has a displayed `name` or `label`
  if (typeof option?.children?.props?.name === "string") return option.children.props.name;
  if (typeof option?.children?.props?.label === "string") return option.children.props.label;

  // If it's an array, find the first string type in the array and take it
  if (Array.isArray(option.label)) return option.label.find((s) => typeof s === "string") ?? "";

  // If children is an array of nodes (e.g. multiple concatenated JSX expressions), join the string members
  if (Array.isArray(option.children)) return option.children.filter((child: unknown) => typeof child === "string").join(" ");

  // :shrug:
  return "";
}

/**
 * @function filterOptionsWithSynonyms
 * @description Case insensitive default select search handler
 */
const filterOptionsWithSynonyms = (searchValue: string, option?: object) =>
  option ? getOptionLabel(option).toLowerCase().includes(searchValue.toLowerCase()) : true;

export const { Option } = AntSelect;
export const { OptGroup } = AntSelect;

export type SelectProps = {
  allowClear?: boolean;
  children?: ReactNode;
  defaultActiveFirstOption?: boolean;
  defaultValue?: AntSelectProps["defaultValue"];
  disabled?: boolean;
  popupMatchSelectWidth?: boolean | number;
  popupClassName?: string;
  dropdownStyle?: CSSProperties;
  filterOption?: AntSelectProps["filterOption"];
  initialValue?: AntSelectProps["defaultValue"];
  label?: ReactNode;
  tooltip?: string;
  message?: string;
  mode?: AntSelectProps["mode"];
  maxTagCount?: AntSelectProps["maxTagCount"];
  name?: NamePath;
  optionFilterProp?: string;
  placeholder?: AntSelectProps["placeholder"];
  rules?: RuleObject[];
  showArrow?: boolean;
  showSearch?: boolean;
  size?: AntSelectProps["size"];
  style?: CSSProperties;
  value?: AntSelectProps["value"];
  optionLabelProp?: string;
  onSearch?: AntSelectProps["onSearch"];
  dataTestId?: string;
  onBlur?: () => void;
  onChange?: AntSelectProps["onChange"];
  onClick?: (e: MouseEvent) => void;
  className?: string;
  labelClassName?: string;
  isLoading?: boolean;
  help?: ReactNode;
  virtual?: boolean;
  filterSort?: AntSelectProps["filterSort"];
  labelRender?: AntSelectProps["labelRender"];
  popupRender?: AntSelectProps["popupRender"];
  notFoundContent?: ReactNode;
};

export const Select = ({
  allowClear = false,
  children,
  defaultActiveFirstOption,
  defaultValue,
  disabled,
  popupMatchSelectWidth,
  popupClassName,
  dropdownStyle,
  filterOption,
  initialValue,
  label,
  tooltip,
  message,
  mode,
  maxTagCount,
  name,
  optionFilterProp,
  placeholder,
  rules,
  showArrow,
  showSearch,
  size,
  style,
  value,
  optionLabelProp = "children",
  onSearch,
  dataTestId,
  onBlur,
  onChange,
  onClick,
  className = "",
  labelClassName = "",
  isLoading,
  help,
  virtual,
  filterSort,
  labelRender,
  popupRender,
  notFoundContent,
}: SelectProps) => {
  const inputElement = useRef<React.ComponentRef<typeof AntSelect>>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(initialValue);
  const [searchValue, setSearchValue] = useState<string>("");

  const effectiveFilterOption = filterOption ?? (showSearch ? filterOptionsWithSynonyms : undefined);
  const effectiveFilterSort = filterSort ?? (showSearch && typeof effectiveFilterOption === "function" ? sortFilteredOptions : undefined);

  const handleSearch: AntSelectProps["onSearch"] = (val) => {
    setSearchValue(val);
    onSearch?.(val);
  };

  // useEffect + setTimeout guarantees we enqueue AFTER any timer rc-select schedules in its own effects (children flush before parents).
  useEffect(() => {
    if (searchValue && effectiveFilterSort) {
      const id = setTimeout(() => {
        inputElement.current?.scrollTo(0);
      }, 0);
      return () => clearTimeout(id);
    }
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlur = () => {
    setHasBeenFocused(false);
    onBlur?.();
  };

  const handleChange: AntSelectProps["onChange"] = (...args) => {
    setInternalValue(args[0]);
    onChange?.(...args);
    // Defer blur by one tick so AntSelect can finish its own focus handling before we blur.
    setTimeout(() => {
      inputElement?.current?.blur();
    }, 1);
  };

  const handleLabelClick = (e: MouseEvent) => {
    setHasBeenFocused(true);
    inputElement.current?.focus();
    onClick?.(e);
  };

  // rc-select falls back to the raw value for values without a matching option (e.g. while options load), which flashes ids.
  const renderLabel: AntSelectProps["labelRender"] = (labeledValue) => {
    const resolvedLabel = labelRender ? labelRender(labeledValue) : labeledValue.label;
    if (resolvedLabel != null || mode === "tags") return resolvedLabel;
    return isLoading ? (
      <span className="floating-select-label-loading">
        <Spin size="small" />
      </span>
    ) : (
      ""
    );
  };

  const active = hasBeenFocused || internalValue != null || value != null;

  const required = rules && rules[0] && rules[0].required;

  const resolvedNotFoundContent =
    notFoundContent !== undefined ? (
      notFoundContent
    ) : (
      <Typography variant="paragraph-m" style={{ textAlign: "center", width: "100%", display: "inline-block" }}>
        {isLoading ? <Spin size="small" /> : <Icon iconName="tabler/ban" />}
      </Typography>
    );

  return (
    <div className="floating-select-wrapper" onClick={handleLabelClick}>
      <Form.Item
        className={`floating-select floating-input ${disabled ? "disabled" : "message"} ${hasBeenFocused ? "active" : ""} ${className}`}
        colon={false}
        label={<InputLabel active={active} required={required} label={label} className={labelClassName} tooltip={tooltip} />}
        name={name}
        rules={parseInputRules(rules, message)}
        initialValue={initialValue}
        help={help}
      >
        <AntSelect
          data-testid={dataTestId}
          allowClear={allowClear}
          defaultActiveFirstOption={defaultActiveFirstOption}
          defaultValue={defaultValue}
          disabled={disabled}
          popupMatchSelectWidth={popupMatchSelectWidth}
          classNames={{ popup: { root: popupClassName } }}
          filterOption={effectiveFilterOption}
          menuItemSelectedIcon={<Icon iconName="tabler/circle-check" />}
          mode={mode}
          maxTagCount={maxTagCount}
          optionFilterProp={showSearch && optionFilterProp === undefined ? "children" : optionFilterProp}
          placeholder={placeholder}
          ref={inputElement}
          showAction={["focus"]}
          showSearch={showSearch}
          size={size}
          style={style}
          value={value}
          onSearch={handleSearch}
          onBlur={handleBlur}
          onChange={handleChange}
          onClick={() => setHasBeenFocused(true)}
          optionLabelProp={optionLabelProp}
          notFoundContent={resolvedNotFoundContent}
          suffixIcon={getSuffixIcon(showArrow, hasBeenFocused, showSearch, mode)}
          styles={{ popup: { root: dropdownStyle } }}
          virtual={virtual}
          filterSort={effectiveFilterSort}
          labelRender={renderLabel}
          popupRender={popupRender}
        >
          {children}
        </AntSelect>
      </Form.Item>
    </div>
  );
};
